/* lib/api/client.js — боевой сетевой примитив (порт из frontend/src/lib/api/client.js, 1:1).
 *
 * Контракт apiFetch: ВСЕГДА резолвится в { ok, status, data } и НЕ бросает по HTTP-кодам
 * (data = распарсенный JSON или null). Это ЗЕРКАЛО мок-контракта fakeApi (тот возвращает
 * payload и бросает err{status,code,detail}); конвертацию делает lib/api/index.js → unwrap().
 *
 * - CSRF: cookie csrftoken → заголовок X-CSRFToken для не-GET/HEAD; ensureCsrf() прогревает cookie.
 * - Workspace: пространство ВКЛАДКИ → заголовок X-Workspace-Id (кроме запросов, переписанных
 *   на публичный демо-слой, — см. demoRewrite). localStorage['restorium.active_workspace_id'] —
 *   общий для вкладок «последний выбор»: с него стартует вкладка, в него пишет явный выбор.
 * - credentials:'same-origin' → сессионная кука ходит ТОЛЬКО через Vite-proxy (см. vite.config.js).
 * - FormData уходит как есть (multipart); остальное — JSON.
 */

function getCsrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? m[1] : null;
}

const WORKSPACE_KEY = 'restorium.active_workspace_id';

/* Сырое значение активного пространства. Браузер с заблокированным хранилищем
 * сайта бросает SecurityError на САМОМ обращении к window.localStorage — без
 * try это роняло apiFetch до запроса, и каждая вкладка публичного демо-стенда
 * (куда такие посетители и приходят) показывала ошибку. Нет доступа = нет выбора. */
function readWorkspaceRaw() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(WORKSPACE_KEY);
  } catch {
    return null;
  }
}

export const WORKSPACE_STORAGE_KEY = WORKSPACE_KEY;

function parseWorkspaceId(raw) {
  if (raw == null || raw === '') return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/* Пространство ЭТОЙ вкладки — источник X-Workspace-Id (kitchen-ux A2 круг 2, S10).
 * Раньше заголовок читался из localStorage на каждый запрос, а localStorage у вкладок общий:
 * соседняя вкладка переключила пространство — и открытая здесь форма пространства A уходила
 * в B (PATCH — 404, создание — в чужое пространство), хотя на экране всё ещё A. Теперь
 * localStorage — только «последний выбор» (с него стартует вкладка, в него пишет явный выбор),
 * а вкладка шлёт своё, пока сама не перейдёт: AuthProvider.adoptWorkspace через сторож
 * несохранённого (app/providers/WorkspaceTabSync.jsx), заголовок меняется в коммите
 * перемонтирования экрана. Память привязана к объекту окна: в браузере он один на вкладку
 * (тесты, подменяющие window, получают новую «вкладку»). */
const TAB = new WeakMap();
/* Выбор вкладки переживает её перезагрузку (круг 3): sessionStorage — свой у каждой вкладки;
 * новая вкладка (пустой sessionStorage) стартует с общего выбора. Хранилище может быть
 * заблокировано — тогда выбор живёт только в памяти. */
const TAB_KEY = 'restorium.tab_workspace_id';
function readTabRaw() {
  if (typeof window === 'undefined') return null;
  try { return window.sessionStorage.getItem(TAB_KEY); } catch { return null; }
}
function writeTabRaw(id) {
  if (typeof window === 'undefined') return;
  try {
    if (id == null) window.sessionStorage.removeItem(TAB_KEY);
    else window.sessionStorage.setItem(TAB_KEY, String(id));
  } catch { /* заблокированное хранилище — только память */ }
}
function tabState() {
  if (typeof window === 'undefined' || !window || typeof window !== 'object') return null;
  let s = TAB.get(window);
  if (!s) {
    s = { id: parseWorkspaceId(readTabRaw()) ?? parseWorkspaceId(readWorkspaceRaw()) };
    TAB.set(window, s);
  }
  return s;
}

export function getActiveWorkspaceId() {
  const s = tabState();
  return s ? s.id : null;
}

/* Общий выбор — что выбрано последним в любой вкладке (для синхронизации вкладок). */
export function getStoredWorkspaceId() {
  return parseWorkspaceId(readWorkspaceRaw());
}

/* Перейти ЭТОЙ вкладкой; общий выбор не трогаем (он уже чужой или тот же). */
export function setTabWorkspaceId(id) {
  const s = tabState();
  if (!s) return;
  s.id = parseWorkspaceId(id == null ? null : String(id));
  writeTabRaw(s.id);
}

/* Закрепить пространство на время операции (kitchen-ux A2 круг 3, S10): id вкладки в НАЧАЛЕ
 * многошаговой операции (поставщик → цена, строки ТТК, разделы меню…) → явный X-Workspace-Id
 * на каждом её шаге; apiFetch явный заголовок не перезаписывает. Вкладка перешла в другое
 * пространство посреди операции — хвост всё равно уходит туда, где операция началась.
 * → опции запроса ({ headers }) для apiFetch / доменных функций; нет пространства — {}. */
export function pinWorkspace(id = getActiveWorkspaceId()) {
  return id != null ? { headers: { 'X-Workspace-Id': String(id) } } : {};
}

/* Замок сессии (kitchen-ux A2 круг 3, T7): сессия браузера общая для вкладок, и если в соседней
 * вошли другим пользователем (или вышли), эта вкладка под старым именем писала бы в чужой
 * аккаунт. WorkspaceTabSync блокирует вкладку экраном и ставит замок: ни один её запрос не
 * уходит, кроме «кто я» и CSRF (по ним видно, вернулся ли прежний пользователь). */
const LOCK = { reason: null };
const LOCK_ALLOW = new Set(['/api/auth/me/', '/api/auth/csrf/']);
export function setSessionLock(reason) {
  LOCK.reason = reason || null;
}
export function sessionLock() {
  return LOCK.reason;
}
function lockedResponse(path) {
  if (!LOCK.reason) return null;
  const clean = typeof path === 'string' ? path.split('?')[0] : '';
  if (LOCK_ALLOW.has(clean)) return null;
  return {
    ok: false, status: 409,
    data: { code: 'session_changed', detail: 'В этом браузере сменилась сессия — действие не выполнено.' },
  };
}

/* Идущая сверка «кто я» (круг 5, S10): пока WorkspaceTabSync сверяет сессию, небезопасные
 * запросы (не GET/HEAD/OPTIONS) ждут её конца. Сверка показала другого пользователя —
 * вкладка заблокирована, и ждавший запрос отбивает замок: запись не уходит от чужого имени.
 * beginSessionCheck() → «сверка закончилась»; перекрывающиеся сверки — до конца последней. */
const CHECK = { pending: null };
export function beginSessionCheck() {
  let finish;
  const p = new Promise((resolve) => { finish = resolve; });
  CHECK.pending = p;
  return () => {
    if (CHECK.pending === p) CHECK.pending = null;
    finish();
  };
}
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
function afterSessionCheck(method, run) {
  if (!CHECK.pending || SAFE_METHODS.has(String(method || 'GET').toUpperCase())) return run();
  return CHECK.pending.then(() => afterSessionCheck(method, run));
}

/* Маркер сессии (круг 5, S10): отдельный общий ключ, который вход, выход и загрузка под другим
 * пользователем переписывают НОВЫМ значением — соседние вкладки получают событие storage и
 * сверяют «кто я», даже когда общий выбор пространства не изменился (вход в то же 20 → 20:
 * setItem того же значения события не даёт). */
export const SESSION_MARKER_KEY = 'restorium.session_marker';
let markerSeq = 0;
export function writeSessionMarker(userId) {
  if (typeof window === 'undefined') return;
  markerSeq += 1;
  const who = userId == null ? 'out' : String(userId);
  try {
    window.localStorage.setItem(SESSION_MARKER_KEY, `${who}:${Date.now()}:${markerSeq}`);
  } catch { /* заблокированное хранилище — соседей не известить */ }
}
/* Чей маркер сейчас: id пользователя строкой | 'out' | null (маркера нет). */
export function sessionMarkerUser() {
  if (typeof window === 'undefined') return null;
  let raw = null;
  try { raw = window.localStorage.getItem(SESSION_MARKER_KEY); } catch { return null; }
  if (!raw) return null;
  const i = raw.indexOf(':');
  return i > 0 ? raw.slice(0, i) : raw;
}

/* 409 { code: 'workspace_unavailable' } — сервер отказал в записи в недоступное пространство
 * (серверная половина — отдельной задачей; фронт готов заранее): слушатели (WorkspaceTabSync)
 * сверяют «кто я» и перечитывают список пространств. */
const UNAVAILABLE = new Set();
export function onWorkspaceUnavailable(fn) {
  UNAVAILABLE.add(fn);
  return () => { UNAVAILABLE.delete(fn); };
}
function noticeUnavailable(status, data, info) {
  if (status !== 409 || !data || data.code !== 'workspace_unavailable') return;
  UNAVAILABLE.forEach((fn) => { try { fn(info); } catch { /* noop */ } });
}

/* Опрос только на чтение (статус хода чата, непрочитанные, уведомления, статусы отправки в POS,
 * привязки Telegram и оплаты): вне счёта запросов в полёте — не держит и не сбрасывает переход
 * вкладки (иначе частый опрос отодвигал бы его без конца). */
export const QUIET_POLL = Object.freeze({ track: false });

/* Переход вкладки ожидает (круг 5, дополнение 3): WorkspaceTabSync ведёт вкладку в пространство,
 * выбранное в соседней, и ждёт закрытия диалогов, тишины сети или ответа человека. Реестры читают
 * признак, чтобы честно сказать «вкладка переходит в другое пространство», а не гадать по общему
 * выбору (localStorage): после «Отмена» в стороже он чужой, а вкладка остаётся в своём.
 * → null | { target, stage: 'wait' | 'dialog' } (dialog — человек отвечает на вопрос сторожа).
 * Снимок неизменяем и тот же, пока признак не менялся (React.useSyncExternalStore). */
const SWITCH = { now: null, listeners: new Set() };
export function setTabSwitchPending(next) {
  const target = next && next.target != null ? Number(next.target) : null;
  const v = target != null && Number.isFinite(target)
    ? Object.freeze({ target, stage: next.stage === 'dialog' ? 'dialog' : 'wait' })
    : null;
  const cur = SWITCH.now;
  if (cur === v || (cur && v && cur.target === v.target && cur.stage === v.stage)) return;
  SWITCH.now = v;
  SWITCH.listeners.forEach((fn) => { try { fn(v); } catch { /* noop */ } });
}
export function tabSwitchPending() {
  return SWITCH.now;
}
export function onTabSwitchPending(fn) {
  SWITCH.listeners.add(fn);
  return () => { SWITCH.listeners.delete(fn); };
}

/* Запросы в полёте: переход вкладки в пространство, выбранное в соседней вкладке, ждёт тишины
 * сети (WorkspaceTabSync) — ни один хвост операции не должен разминуться с её пространством.
 * started — сколько учтённых запросов начато всего: переход сверяет, что с момента тишины
 * не началось ничего нового. */
const NET = { inFlight: 0, started: 0, listeners: new Set() };
function netChanged() {
  NET.listeners.forEach((fn) => { try { fn(); } catch { /* noop */ } });
}
export function requestsInFlight() {
  return NET.inFlight;
}
export function requestsStarted() {
  return NET.started;
}
/* Сколько ожиданий тишины сети подписано сейчас (whenNetworkQuiet): ожидание, ставшее ненужным
 * (решение в тосте, отмена перехода), снимается сразу, а не висит до конца загрузки. */
export function networkWaiters() {
  return NET.listeners.size;
}
/* → Promise<boolean>: true — запросов в полёте нет уже ms миллисекунд подряд (новый запрос окно
 * сбрасывает); false — ожидание отменено (signal: пришло новое событие соседней вкладки),
 * слушатель и таймеры сняты. Потолок молчаливого ожидания (круг 4): дольше slowAfterMs —
 * onSlow() один раз (вкладка скажет, что переключится после загрузки), ожидание продолжается. */
export function whenNetworkQuiet(ms = 300, { signal, slowAfterMs, onSlow } = {}) {
  if (signal && signal.aborted) return Promise.resolve(false);
  return new Promise((resolve) => {
    let timer = null;
    let slowTimer = null;
    let onAbort = null;
    const check = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      if (NET.inFlight > 0) return;
      timer = setTimeout(() => {
        timer = null;
        if (NET.inFlight > 0) return;
        finish(true);
      }, ms);
    };
    function finish(v) {
      if (timer) { clearTimeout(timer); timer = null; }
      if (slowTimer) { clearTimeout(slowTimer); slowTimer = null; }
      NET.listeners.delete(check);
      if (signal && onAbort) signal.removeEventListener('abort', onAbort);
      resolve(v);
    }
    onAbort = () => finish(false);
    NET.listeners.add(check);
    if (signal) signal.addEventListener('abort', onAbort);
    if (typeof onSlow === 'function' && slowAfterMs > 0) {
      slowTimer = setTimeout(() => {
        slowTimer = null;
        try { onSlow(); } catch { /* noop */ }
      }, slowAfterMs);
    }
    check();
  });
}
async function tracked(run) {
  NET.inFlight += 1;
  NET.started += 1;
  netChanged();
  try {
    return await run();
  } finally {
    NET.inFlight = Math.max(0, NET.inFlight - 1);
    netChanged();
  }
}

/* Пауза ОПЕРАЦИИ — в счёте «в полёте», как запрос (idempotency-key, S10): 1,5 с перед автоматическим
 * повтором создания с ключом (retryOnceIfUnknown) — часть попытки. Без счёта сторож перехода вкладки
 * (WorkspaceTabSync) видел тишину сети посреди попытки и переводил вкладку: продолжение редактора
 * (stayOnCreated → replaceTop) шло уже после размонтирования. Опросы QUIET_POLL этой паузой не
 * пользуются — их семантика прежняя. → Promise, который резолвится через ms. */
export function trackedPause(ms) {
  return tracked(() => new Promise((resolve) => { setTimeout(resolve, ms); }));
}

/* Явный выбор в этой вкладке (вход, переключатель, выход): и заголовок, и общий выбор —
 * соседние вкладки узнают о нём событием storage. */
export function setActiveWorkspaceId(id) {
  if (typeof window === 'undefined') return;
  setTabWorkspaceId(id);
  try {
    if (id == null) window.localStorage.removeItem(WORKSPACE_KEY);
    else window.localStorage.setItem(WORKSPACE_KEY, String(id));
  } catch {
    /* заблокированное хранилище — выбор живёт только в этой вкладке */
  }
}

// Публичный демо-стенд (/business-demo) отдаёт iiko-аналитику read-only и БЕЗ
// сессии через отдельный AllowAny-слой /api/demo/iiko/*. Пока открыт демо-маршрут,
// переписываем ТОЛЬКО запросы интеграций на демо-эндпоинты — экраны отчётов
// переиспользуются как есть, а всё прочее (csrf, auth) не трогаем. Ключуемся по
// текущему пути (не по мутабельному флагу), поэтому переписывание самоограничено
// демо-страницей и сбрасывается при уходе с неё.
const DEMO_PREFIX = '/api/integrations/';

/* Открыт ли сейчас публичный демо-стенд. Единственная реализация проверки пути
 * в сетевом слое: на ней стоят и переписывание запросов, и область хранения
 * ниже. Семантика обязана совпадать с isDemoRoute() демо-моста экранов
 * (screens/demo/demoAccess.jsx) — это держит тест lib/api/demoScope.test.js. */
export function isDemoPath() {
  return typeof window !== 'undefined' && !!window.location
    && typeof window.location.pathname === 'string'
    && window.location.pathname.startsWith('/business-demo');
}

export function demoRewrite(path) {
  if (!isDemoPath()) return path;
  if (typeof path === 'string' && path.startsWith(DEMO_PREFIX)) {
    return '/api/demo/iiko/' + path.slice(DEMO_PREFIX.length);
  }
  return path;
}

/* Область хранения удобств Аналитики одного зрителя: столбцы таблиц, период,
 * ставка эквайринга (ключи вида restorium.table.<область>.<таблица>).
 *
 * Демо-стенд — СВОЯ область и никогда не берёт чужую. Демо-страница не трогает
 * restorium.active_workspace_id (гость без сессии, его сбрасывает только выход),
 * поэтому менеджер, показывающий демо из своего браузера, получал в демо скрытые
 * столбцы и период своего живого ресторана, а переключив что-то в демо — молча
 * перекраивал их у себя. Период при этом ещё и врал: диапазон живого ресторана
 * обрезался границами демо-данных, и «90 дней» показывали пару недель.
 *
 * Вне демо — пространство вкладки или 'x', байт-в-байт как прежние ключи (id
 * пишется в хранилище как String(id)): сохранённые настройки пользователей не
 * теряются. Пространство — вкладки, а не общий выбор: соседняя вкладка, выбравшая
 * другое пространство, не перекраивает настройки открытого здесь. */
export function analyticsStorageScope() {
  if (isDemoPath()) return 'demo';
  const raw = readWorkspaceRaw();
  if (!raw) return 'x';
  const tab = getActiveWorkspaceId();
  return tab != null ? String(tab) : raw;
}

/* track:false — запрос вне счёта «в полёте»: только для сверок, которые сами решают о переходе
 * вкладки («кто я» в WorkspaceTabSync). Такой запрос не часть операции экрана, а в счёте он
 * отодвигал бы ту самую тишину сети, которой переход ждёт. */
export function apiFetch(path, opts) {
  return afterSessionCheck(opts && opts.method, () => {
    const locked = lockedResponse(path);
    if (locked) return Promise.resolve(locked);
    const { track = true, ...rest } = opts || {};
    return track ? tracked(() => apiFetchNow(path, rest)) : apiFetchNow(path, rest);
  });
}

/* Сырой fetch (SSE-стримы чата и саммари документов) — через те же замок сессии и счётчик
 * запросов в полёте, что apiFetch (kitchen-ux A2 круг 4). consume(res) дочитывает ответ: запрос
 * «в полёте», пока consume не закончил, — стрим держит вкладку от перехода до конца ответа.
 * Под замком сети нет: consume получает ответ 409 { code: 'session_changed' } — обычную ветку
 * ошибки вызывающего. Сетевой отказ fetch — отказ промиса, как у самого fetch. */
export function trackedFetch(url, init, consume = (res) => res) {
  return afterSessionCheck(init && init.method, () => {
    const locked = lockedResponse(url);
    if (locked) return Promise.resolve().then(() => consume(lockedHttpResponse(locked)));
    return tracked(async () => consume(await fetch(url, init)));
  });
}
function lockedHttpResponse(locked) {
  const text = JSON.stringify(locked.data);
  if (typeof Response === 'function') {
    return new Response(text, { status: locked.status, headers: { 'Content-Type': 'application/json' } });
  }
  return {
    ok: false, status: locked.status, body: null,
    headers: { get: (k) => (/content-type/i.test(k) ? 'application/json' : null) },
    json: async () => locked.data, text: async () => text,
  };
}

async function apiFetchNow(path, { method = 'GET', body, headers, ...rest } = {}) {
  const target = demoRewrite(path);
  // Публичная демо-страница анонимна и ни одному её запросу пространство не нужно:
  // ни переписанным отчётам, ни заявке (/api/demo/lead/), ни CSRF. Поэтому признак —
  // сама страница, а не «запрос переписан»: иначе заявка с демо уносила бы id живого
  // пространства посетителя, открывшего стенд из своего браузера.
  const demo = isDemoPath();
  path = target;
  const finalHeaders = { Accept: 'application/json', ...headers };
  if (body !== undefined && !(body instanceof FormData)) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (method !== 'GET' && method !== 'HEAD') {
    const token = getCsrfToken();
    if (token) finalHeaders['X-CSRFToken'] = token;
  }
  // Активный workspace — бэк fallback'нёт на personal, если заголовка нет
  // или id не валиден. Передаём всегда, когда выбор уже зафиксирован.
  const wsId = demo ? null : getActiveWorkspaceId();
  if (wsId && finalHeaders['X-Workspace-Id'] === undefined) {
    finalHeaders['X-Workspace-Id'] = String(wsId);
  }

  const res = await fetch(path, {
    method,
    headers: finalHeaders,
    credentials: 'same-origin',
    body: body !== undefined ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
    ...rest,
  });

  let data = null;
  if (res.status !== 204) {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try { data = await res.json(); } catch { /* noop */ }
    }
  }

  noticeUnavailable(res.status, data, { path, method, workspaceId: finalHeaders['X-Workspace-Id'] || null });
  return { ok: res.ok, status: res.status, data };
}

export async function ensureCsrf() {
  if (!getCsrfToken()) {
    await apiFetch('/api/auth/csrf/');
  }
}

/* Имя файла из Content-Disposition: сперва RFC 5987 filename*=UTF-8''…,
 * затем обычный filename="…" / filename=…; null, если заголовка нет. */
export function filenameFromDisposition(header) {
  if (!header) return null;
  const star = header.match(/filename\*\s*=\s*utf-8''([^;]+)/i);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch { /* noop */ }
  }
  const plain = header.match(/filename\s*=\s*"([^"]*)"/i) || header.match(/filename\s*=\s*([^;]+)/i);
  const name = plain ? plain[1].trim() : null;
  return name || null;
}

/* Скачивание файла (docx/zip/pdf/xlsx) БЕЗ window.open: браузерная навигация
 * не шлёт X-Workspace-Id, бэк фолбэчится на personal-workspace и сущности
 * business-пространств ловят 404 (скачивался «битый» файл вместо ошибки).
 * Качаем fetch'ем с теми же заголовками, что apiFetch, и сохраняем blob.
 * Контракт зеркалит apiFetch: { ok, status, data } без throw; data — JSON
 * тела ошибки (для тоста) или null. filename-опция форсирует имя, иначе
 * берём его из Content-Disposition ответа.
 *
 * method/body — для выгрузок, чьи параметры не помещаются в адрес (выбор групп
 * ABC, см. lib/api/urlBudget.js). Это по-прежнему чтение, просто параметры едут
 * телом; сессионная авторизация DRF на не-GET требует CSRF — греем куку и шлём
 * заголовок, как это делает apiFetch.
 *
 * Пространство — как у apiFetch: по умолчанию вкладки; закреплённое операцией (S10) —
 * headers из pinWorkspace() ({ ...pinWorkspace(), filename }) или короче workspaceId.
 * Явный заголовок X-Workspace-Id главнее workspaceId, и пространством вкладки ни то ни
 * другое не перезаписывается.
 *
 * headers — заголовки ОТВЕТА как есть (Headers fetch, у отказа тоже): выгрузка говорит в них
 * то, чего нет в файле (реестр ТТК — X-TTK-Export-Skipped: кого сервер не положил в архив).
 * Отказ замка сессии — без сети, заголовков нет. */
export function downloadFile(path, opts) {
  return afterSessionCheck(opts && opts.method, () => {
    const locked = lockedResponse(path);
    if (locked) return Promise.resolve(locked);
    return tracked(() => downloadFileNow(path, opts));
  });
}

async function downloadFileNow(path, { filename, method = 'GET', body, headers: own, workspaceId } = {}) {
  const target = demoRewrite(path);
  const headers = { ...own };
  if (headers['X-Workspace-Id'] === undefined && workspaceId != null) headers['X-Workspace-Id'] = String(workspaceId);
  // демо-страница — без пространства вкладки, по тому же признаку, что и apiFetch
  const wsId = isDemoPath() ? null : getActiveWorkspaceId();
  if (wsId && headers['X-Workspace-Id'] === undefined) headers['X-Workspace-Id'] = String(wsId);
  if (method !== 'GET' && method !== 'HEAD') {
    await ensureCsrf();
    const token = getCsrfToken();
    if (token) headers['X-CSRFToken'] = token;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(target, {
    method,
    credentials: 'same-origin',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let data = null;
    if ((res.headers.get('content-type') || '').includes('application/json')) {
      try { data = await res.json(); } catch { /* noop */ }
    }
    noticeUnavailable(res.status, data, { path: target, method, workspaceId: headers['X-Workspace-Id'] || null });
    return { ok: false, status: res.status, data, headers: res.headers };
  }
  const blob = await res.blob();
  const name = filename
    || filenameFromDisposition(res.headers.get('content-disposition'))
    || 'download';
  const objUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objUrl;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objUrl);
  return { ok: true, status: res.status, data: null, headers: res.headers };
}
